import { useQuery } from '@tanstack/react-query';
import { Activity, Check, Copy, Database, GitCommitHorizontal, Server } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { DuplicateRules } from '../components/candidates/duplicate-rules.tsx';
import { Badge } from '../components/ui/badge.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.tsx';
import { api as legacyApi, describeError } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { THEMES, useSettings, useTheme, type Settings, type Theme } from '../lib/settings.ts';

const THEME_LABEL: Record<Theme, string> = { system: 'Match system', light: 'Light', dark: 'Dark' };

export function SettingsPage() {
  const session = useSession();
  const [settings, update] = useSettings(session.me.user.id);
  const [theme, setTheme] = useTheme();
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Settings"
        description={
          <>
            Signed in as{' '}
            <span className="text-foreground font-medium">{session.me.user.email}</span>.
            Preferences are yours; they do not change the organisation.
          </>
        }
      />
      <div className="flex flex-col gap-6">
        <ApiStatus />
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Preferences</CardTitle>
              <CardDescription>How the app looks and how lists load for you.</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="divide-border flex flex-col divide-y py-2">
            <Field label="Appearance" hint="Stored on this device.">
              <Select value={theme} onValueChange={(v) => setTheme(v as Theme)}>
                <SelectTrigger aria-label="Appearance" className="w-full sm:w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {THEMES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {THEME_LABEL[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Pagination" hint="How lists load: scroll forever, or page by page.">
              <Select
                value={settings.pagination}
                onValueChange={(v) => update({ pagination: v as Settings['pagination'] })}
              >
                <SelectTrigger aria-label="Pagination" className="w-full sm:w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="infinite">Infinite scroll</SelectItem>
                  <SelectItem value="paged">Pages</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field
              label="Rows per request"
              hint="Larger pages mean fewer requests while scrolling."
            >
              <Select
                value={String(settings.pageSize)}
                onValueChange={(v) => update({ pageSize: Number(v) as Settings['pageSize'] })}
              >
                <SelectTrigger aria-label="Rows per request" className="w-full sm:w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[50, 100, 200].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </CardContent>
        </Card>
        {session.isEnabled('candidates.enabled') && (
          <Card>
            <CardContent>
              <DuplicateRules />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

/**
 * The API's /health, polled every 30s. Moved here from Home (TS-1591): it is diagnostics, not
 * something to greet people with. The version is a 40-character commit, so it shows the short
 * form and copies the full one; the long value never sets the card's width.
 */
function ApiStatus() {
  const health = useQuery({
    queryKey: ['health'],
    queryFn: legacyApi.health,
    retry: false,
    refetchInterval: 30_000,
  });
  const ok = health.data?.status === 'ok';
  return (
    <Card data-testid="api-status">
      <CardHeader>
        <div className="flex min-w-0 items-center gap-3">
          <span
            className={
              ok
                ? 'bg-state-success-bg text-state-success-fg grid size-9 shrink-0 place-items-center rounded-xl'
                : 'bg-muted text-muted-foreground grid size-9 shrink-0 place-items-center rounded-xl'
            }
          >
            <Activity className="size-[18px]" aria-hidden />
          </span>
          <div className="min-w-0">
            <CardTitle>API status</CardTitle>
            <CardDescription>Checked every 30 seconds.</CardDescription>
          </div>
        </div>
        {health.data && (
          <span data-testid="health-status">
            <Badge variant={ok ? 'success' : 'warning'}>
              <span className="size-1.5 rounded-full bg-current" aria-hidden />
              {health.data.status}
            </Badge>
          </span>
        )}
      </CardHeader>
      <CardContent>
        {health.isPending && <p className="text-muted-foreground text-sm">Checking…</p>}
        {health.isError && (
          <p className="text-danger text-sm" role="alert">
            {describeError(health.error)}
          </p>
        )}
        {health.data && (
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Stat icon={GitCommitHorizontal} label="Version">
              <Version value={health.data.version} />
            </Stat>
            <Stat icon={Database} label="Database">
              <CheckValue value={health.data.checks.db} />
            </Stat>
            <Stat icon={Server} label="Redis">
              <CheckValue value={health.data.checks.redis} />
            </Stat>
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Database;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-muted/60 border-border min-w-0 rounded-xl border px-4 py-3">
      <dt className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
        <Icon className="size-3.5" aria-hidden /> {label}
      </dt>
      <dd className="mt-1 min-w-0 text-sm font-medium">{children}</dd>
    </div>
  );
}

function CheckValue({ value }: { value: string }) {
  return (
    <span className={value === 'ok' ? 'text-state-success-fg' : 'text-state-warning-fg'}>
      {value === 'ok' ? 'Healthy' : value}
    </span>
  );
}

/** A commit sha shown short (7), copied whole; anything else (a dev label) shown as is, truncated. */
function Version({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const isSha = /^[0-9a-f]{40}$/.test(value);
  const copy = () => {
    void navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <code className="truncate font-mono text-[13px]" title={value} data-testid="health-version">
        {isSha ? value.slice(0, 7) : value}
      </code>
      {isSha && (
        <button
          type="button"
          onClick={copy}
          className="text-muted-foreground hover:text-foreground hover:bg-surface-hover shrink-0 rounded-md p-1 transition-colors"
          aria-label={copied ? 'Copied' : 'Copy full version'}
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </button>
      )}
    </span>
  );
}

function Field({ label, hint, children }: { label: string; hint: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-2 py-3 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-6">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-muted-foreground text-sm">{hint}</p>
      </div>
      {children}
    </div>
  );
}
